/* ==========================================================================
   Case Studies Page Component (100% Multilingual & 9-Item Pagination)
   ========================================================================== */

import { t, getLocale } from '../locales/i18n.js';
import { openArticleModal } from '../components/ArticleModal.js';
import { getArticlesByCategory, subscribeArticleStore } from '../services/articleStore.js';
import { articleIndexTranslations } from '../locales/articleTranslationsData.js';

const caseStudiesUi = {
  en: {
    breadcrumbHome: 'Home',
    breadcrumbNews: 'News',
    breadcrumbCurrent: 'Case Studies',
    eyebrow: 'PROVEN FIELD DEPLOYMENTS',
    title: 'Case Studies & Field Reports',
    subtitle: 'Discover how Sound Event Detector protects cities, hospitals, energy grids, and campuses across the globe.',
    countBadge: (cur, tot, p, tp) => `Showing ${cur} of ${tot} case studies (Page ${p} of ${tp})`,
    readBtn: 'Read Case Study →',
    prev: '← Prev',
    next: 'Next →',
    defaultDate: 'Field Report',
    defaultLocation: 'European Field Deployment'
  },
  cs: {
    breadcrumbHome: 'Domů',
    breadcrumbNews: 'Novinky',
    breadcrumbCurrent: 'Případové studie',
    eyebrow: 'OVĚŘENÁ REÁLNÁ NASAZENÍ',
    title: 'Případové studie a zprávy z terénu',
    subtitle: 'Zjistěte, jak Sound Event Detector chrání města, nemocnice, energetické sítě a kampusy po celém světě.',
    countBadge: (cur, tot, p, tp) => `Zobrazeno ${cur} z ${tot} případových studií (Strana ${p} z ${tp})`,
    readBtn: 'Přečíst případovou studii →',
    prev: '← Předchozí',
    next: 'Další →',
    defaultDate: 'Zpráva z nasazení',
    defaultLocation: 'Evropské nasazení'
  },
  de: {
    breadcrumbHome: 'Startseite',
    breadcrumbNews: 'Nachrichten',
    breadcrumbCurrent: 'Fallstudien',
    eyebrow: 'BEWÄHRTE PRAXISEINSÄTZE',
    title: 'Fallstudien & Einsatzberichte',
    subtitle: 'Erfahren Sie, wie der Sound Event Detector Städte, Krankenhäuser, Energienetze und Bildungseinrichtungen weltweit schützt.',
    countBadge: (cur, tot, p, tp) => `Zeige ${cur} von ${tot} Fallstudien (Seite ${p} von ${tp})`,
    readBtn: 'Fallstudie lesen →',
    prev: '← Zurück',
    next: 'Weiter →',
    defaultDate: 'Einsatzbericht',
    defaultLocation: 'Europäischer Feldeinsatz'
  },
  es: {
    breadcrumbHome: 'Inicio',
    breadcrumbNews: 'Noticias',
    breadcrumbCurrent: 'Estudios de caso',
    eyebrow: 'DESPLIEGUES REALES COMPROBADOS',
    title: 'Estudios de Caso e Informes de Campo',
    subtitle: 'Descubra cómo Sound Event Detector protege ciudades, hospitales, redes energéticas y campus en todo el mundo.',
    countBadge: (cur, tot, p, tp) => `Mostrando ${cur} de ${tot} casos de estudio (Página ${p} de ${tp})`,
    readBtn: 'Leer caso de estudio →',
    prev: '← Anterior',
    next: 'Siguiente →',
    defaultDate: 'Informe de campo',
    defaultLocation: 'Despliegue europeo'
  },
  it: {
    breadcrumbHome: 'Home',
    breadcrumbNews: 'Notizie',
    breadcrumbCurrent: 'Casi di studio',
    eyebrow: 'APPLICAZIONI REALI COMPROVATE',
    title: 'Casi di Studio e Rapporti dal Campo',
    subtitle: 'Scopri come Sound Event Detector protegge città, ospedali, reti energetiche e università in tutto il mondo.',
    countBadge: (cur, tot, p, tp) => `Visualizzazione di ${cur} su ${tot} casi di studio (Pagina ${p} di ${tp})`,
    readBtn: 'Leggi caso di studio →',
    prev: '← Precedente',
    next: 'Successivo →',
    defaultDate: 'Rapporto dal campo',
    defaultLocation: 'Installazione europea'
  },
  pt: {
    breadcrumbHome: 'Início',
    breadcrumbNews: 'Notícias',
    breadcrumbCurrent: 'Estudos de caso',
    eyebrow: 'IMPLANTAÇÕES REAIS COMPROVADAS',
    title: 'Estudos de Caso e Relatórios de Campo',
    subtitle: 'Descubra como o Sound Event Detector protege cidades, hospitais, redes de energia e universidades em todo o mundo.',
    countBadge: (cur, tot, p, tp) => `Exibindo ${cur} de ${tot} estudos de caso (Página ${p} de ${tp})`,
    readBtn: 'Ler estudo de caso →',
    prev: '← Anterior',
    next: 'Próximo →',
    defaultDate: 'Relatório de campo',
    defaultLocation: 'Implantação europeia'
  },
  ar: {
    breadcrumbHome: 'الرئيسية',
    breadcrumbNews: 'الأخبار',
    breadcrumbCurrent: 'دراسات الحالة',
    eyebrow: 'عمليات نشر ميدانية مثبتة',
    title: 'دراسات الحالة والتقارير الميدانية',
    subtitle: 'اكتشف كيف يحمي كاشف الأصوات SED المدن والمستشفيات وشبكات الطاقة والمجمعات التعليمية حول العالم.',
    countBadge: (cur, tot, p, tp) => `عرض ${cur} من أصل ${tot} دراسة حالة (صفحة ${p} من ${tp})`,
    readBtn: 'قراءة دراسة الحالة ←',
    prev: '← السابق',
    next: 'التالي ←',
    defaultDate: 'تقرير ميداني',
    defaultLocation: 'نشر ميداني في أوروبا'
  }
};

const formatDate = (dStr, lang) => {
  if (!dStr) return '';
  const d = new Date(dStr);
  if (isNaN(d.getTime())) return dStr;
  if (lang === 'cs') {
    return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
  }
  return dStr;
};

export function renderCaseStudiesPage(onNavigate) {
  const container = document.createElement('div');
  container.className = 'sed-page';
  const lang = getLocale() || 'en';
  const ui = caseStudiesUi[lang] || caseStudiesUi.en;

  const parseDate = (dStr) => {
    if (!dStr) return 0;
    const d = new Date(dStr);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  };

  const buildCaseStudiesList = () => {
    const rawList = getArticlesByCategory('case-studies');
    return rawList.map(data => {
      if (!data) return null;
      const safeSlug = data.slug || 'case-study';
      const trans = (data.translations && data.translations[lang]) || (articleIndexTranslations[lang] && articleIndexTranslations[lang][safeSlug]) || {};
      const title = trans.title || data.title || safeSlug.replace(/-/g, ' ').toUpperCase();
      const category = trans.category || data.tag || (lang === 'cs' ? 'Veřejná bezpečnost' : 'Public Safety');
      const desc = trans.excerpt || data.excerpt || (data.content ? data.content.replace(/<[^>]+>/g, '').slice(0, 160) + '...' : "Official Sound Event Detector field study report.");

      return {
        id: safeSlug,
        title: title,
        category: category,
        date: formatDate(data.date, lang) || ui.defaultDate,
        location: ui.defaultLocation,
        img: data.img || "/images/2.png",
        desc: desc,
        yt: data.yt || ""
      };
    }).filter(Boolean);
  };

  let caseStudiesList = buildCaseStudiesList();

  const ITEMS_PER_PAGE = 9;
  let currentPage = 1;

  container.innerHTML = `
    <!-- Header Hero -->
    <section class="sed-section" style="background: linear-gradient(165deg, var(--navy-dark) 0%, var(--blue-dark) 100%); color: #ffffff; padding: 4.5rem 0 3.5rem;">
      <div class="sed-container">
        <div style="display: flex; gap: 0.5rem; align-items: center; font-size: 0.85rem; color: #cbd5e1; margin-bottom: 1.5rem; flex-wrap: wrap;">
          <a href="#home" style="color: #ffffff; text-decoration: none;" data-route="home">${ui.breadcrumbHome}</a> /
          <a href="#news" style="color: #ffffff; text-decoration: none;" data-route="news">${ui.breadcrumbNews}</a> /
          <span style="color: var(--blue-primary); font-weight: 700;">${ui.breadcrumbCurrent}</span>
        </div>
        <div class="sed-section-header" style="max-width: 840px; text-align: left; margin: 0;">
          <div class="sed-eyebrow" style="background: rgba(255,255,255,0.1); color: var(--blue-primary); border-color: rgba(255,255,255,0.2);">${ui.eyebrow}</div>
          <h1 class="sed-page-hero-title" style="color: #ffffff;">${ui.title}</h1>
          <p style="font-size: 1.15rem; color: #e2e8f0; line-height: 1.65; margin-bottom: 0;">${ui.subtitle}</p>
        </div>
      </div>
    </section>

    <!-- Case Studies Grid with 9-Item Pagination (3 Columns on Desktop) -->
    <section class="sed-section" style="background-color: #ffffff; padding: 3.5rem 0 5rem;">
      <div class="sed-container">
        <div style="font-size: 0.88rem; font-weight: 700; color: var(--text-muted); margin-bottom: 1.5rem;" id="case-studies-count-badge">
          ${ui.countBadge(Math.min(9, caseStudiesList.length), caseStudiesList.length, 1, Math.ceil(caseStudiesList.length/9))}
        </div>

        <div id="sed-casestudies-grid" class="sed-grid-3">
          <!-- Paginated Cards (Max 9) -->
        </div>

        <!-- Pagination Controls -->
        <div id="sed-casestudies-pagination" style="display: flex; justify-content: center; align-items: center; gap: 0.5rem; margin-top: 2.5rem; flex-wrap: wrap;">
          <!-- Page Buttons -->
        </div>
      </div>
    </section>
  `;

  function renderPage(page) {
    const gridEl = container.querySelector('#sed-casestudies-grid');
    const pagEl = container.querySelector('#sed-casestudies-pagination');
    const badgeEl = container.querySelector('#case-studies-count-badge');
    if (!gridEl) return;

    const totalPages = Math.ceil(caseStudiesList.length / ITEMS_PER_PAGE) || 1;
    if (page < 1) page = 1;
    if (page > totalPages) page = totalPages;
    currentPage = page;

    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const items = caseStudiesList.slice(start, start + ITEMS_PER_PAGE);

    if (badgeEl) {
      badgeEl.innerText = ui.countBadge(items.length, caseStudiesList.length, currentPage, totalPages);
    }

    gridEl.innerHTML = items.map(cs => `
      <div class="sed-card" style="padding: 0; overflow: hidden; cursor: pointer; display: flex; flex-direction: column; justify-content: space-between; border: 1.5px solid var(--slate-line); border-radius: 14px; box-shadow: 0 4px 15px rgba(0,0,0,0.03);" data-casestudy-id="${cs.id}">
        <div>
          <div style="height: 180px; overflow: hidden; background-color: var(--navy-dark); position: relative;">
            <img src="${cs.img}" alt="${cs.title}" style="width: 100%; height: 100%; object-fit: cover;" onerror="this.onerror=null; this.src='/images/SED_Camera-Edition-2048x1365.png';">
            <div style="position: absolute; top: 12px; left: 12px; background: rgba(10, 25, 47, 0.85); color: #ffffff; padding: 0.25rem 0.65rem; border-radius: var(--radius-badge); font-size: 0.72rem; font-weight: 800; backdrop-filter: blur(4px);">
              ${cs.category}
            </div>
            ${cs.date ? `
              <div style="position: absolute; bottom: 12px; right: 12px; background: rgba(53, 168, 223, 0.9); color: #ffffff; padding: 0.2rem 0.6rem; border-radius: 4px; font-size: 0.72rem; font-weight: 700;">
                ${cs.date}
              </div>
            ` : ''}
          </div>
          <div style="padding: 1.5rem 1.5rem 1rem;">
            <h3 class="sed-card-title" style="word-break: break-word;">${cs.title}</h3>
            <p class="sed-card-text" style="font-size: 0.9rem; line-height: 1.6; color: var(--text-dark); margin: 0;">${cs.desc}</p>
          </div>
        </div>
        <div style="padding: 0 1.5rem 1.5rem; display: flex; gap: 0.5rem; align-items: center;">
          <button class="sed-btn sed-btn-primary btn-card-read" style="flex: 1; font-size: 0.88rem;">${ui.readBtn}</button>
          <button class="sed-btn sed-btn-outline btn-card-share" data-share-url="https://www.jalud.eu/case-studies/${cs.id}" style="padding: 0.55rem 0.75rem; font-size: 0.85rem; color: var(--navy-dark); border-color: var(--slate-line);" title="Copy direct link / Kopírovat odkaz">🔗</button>
        </div>
      </div>
    `).join('');

    gridEl.querySelectorAll('[data-casestudy-id]').forEach(card => {
      card.addEventListener('click', (e) => {
        if (e.target.closest('.btn-card-share')) return;
        const csId = card.getAttribute('data-casestudy-id');
        openArticleModal(csId);
      });
    });

    gridEl.querySelectorAll('.btn-card-share').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const url = btn.getAttribute('data-share-url');
        navigator.clipboard.writeText(url).then(() => {
          const orig = btn.innerText;
          btn.innerText = '✓';
          btn.style.borderColor = '#10b981';
          btn.style.color = '#10b981';
          setTimeout(() => {
            btn.innerText = orig;
            btn.style.borderColor = '';
            btn.style.color = '';
          }, 2000);
        }).catch(() => {
          prompt('Direct link:', url);
        });
      });
    });

    if (pagEl) {
      if (totalPages <= 1) {
        pagEl.innerHTML = '';
      } else {
        const prevText = ui.prev;
        const nextText = ui.next;
        let pagHtml = `
          <button class="sed-btn sed-btn-outline btn-cs-page" data-page="${currentPage - 1}" ${currentPage === 1 ? 'disabled style="opacity:0.4; cursor:not-allowed;"' : ''}>${prevText}</button>
        `;
        for (let i = 1; i <= totalPages; i++) {
          const active = i === currentPage;
          pagHtml += `
            <button class="sed-btn ${active ? 'sed-btn-primary' : 'sed-btn-outline'} btn-cs-page" data-page="${i}" style="${active ? '' : 'color: var(--navy-dark); border-color: var(--slate-line);'} font-weight: 700; padding: 0.4rem 0.85rem;">${i}</button>
          `;
        }
        pagHtml += `
          <button class="sed-btn sed-btn-outline btn-cs-page" data-page="${currentPage + 1}" ${currentPage === totalPages ? 'disabled style="opacity:0.4; cursor:not-allowed;"' : ''}>${nextText}</button>
        `;
        pagEl.innerHTML = pagHtml;

        pagEl.querySelectorAll('.btn-cs-page').forEach(btn => {
          btn.addEventListener('click', () => {
            const p = parseInt(btn.getAttribute('data-page'), 10);
            if (!isNaN(p) && p >= 1 && p <= totalPages) {
              renderPage(p);
              const b = container.querySelector('#case-studies-count-badge');
              if (b && b.offsetTop) {
                window.scrollTo({ top: b.offsetTop - 100, behavior: 'smooth' });
              }
            }
          });
        });
      }
    }
  }

  // Initial render
  renderPage(1);

  // Re-render when new case studies arrive from API
  subscribeArticleStore(() => {
    caseStudiesList = buildCaseStudiesList();
    renderPage(currentPage);
  });

  // Attach navigation listeners
  container.querySelectorAll('[data-route]').forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      if (onNavigate) onNavigate(link.getAttribute('data-route'));
    });
  });

  return container;
}
